import {
    AdditionalDistributionLayer,
    Charity,
    DistributionType,
    EstateFlowchartState,
    EstateMember,
    EstateToggle,
    FamilyMembers,
    FiduciaryAssignment,
    FiduciaryMember,
    FlowchartEstateItemType,
    Member,
    NonTrustDistribution,
    Outright,
    RevocableTrust,
    State,
    Trust,
    Trustee
} from "../model/EstateFlowchartTypes";
import {MemberGroup, MemberGroupMember} from "../../ClientManagement/models/InvestorGroupType";
import {PowerOfAppointment} from "../components/PowersOfAppointmentSection/PowerOfAppointment";
import {DropdownItem} from "../components/FormContent/DropdownField/DropdownField";
import {WillSummary} from "../model/WillSummary";
import {IrrevocableTrust} from "../model/IrrevocableTrust";
import {Will} from "../model/Will";
import {Miscellaneous} from "../model/Miscellaneous";

export const isUUID = (text: string) => {
    const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return regex.test(text);
}

/**
 * Checks if a given member ID is not present in the list of family members.
 *
 * @param member_id - The ID of the member to check.
 * @param familyMembers - An object containing the family members.
 * @returns `true` if the member ID is not found in the family members list, otherwise `false`.
 */
export const isCustomMember = (member_id: string, familyMembers: FamilyMembers) => {
    // compare member_id against list of member_id's from the family tree
    const memberIds: string[] = familyMembers.familyMember.map(member => member.value);
    return !memberIds.includes(member_id) && member_id !== familyMembers.value;
}

/**
 * Evaluates the member ID for use as an option in MemberDropdownMultiselect based on its value and label,
 * as well as the provided list of parsed family members.
 *
 * @param element - The dropdown element containing `value` and `label` properties.
 * @param parsedFamilyMembers - The list of parsed family members used for validation.
 * @returns The evaluated member ID as a string, an empty string for specific cases,
 *          or `undefined` if the value does not meet the criteria.
 */
export const evaluateDropdownMemberId = (element: any, parsedFamilyMembers: FamilyMembers): string | undefined => {
    if (!isCustomMember(element.value, parsedFamilyMembers)) {
        return element.value
    } else if (element.label === 'Northern Trust' && element.value === "") {
        return ""
    } else if (isCustomMember(element.value, parsedFamilyMembers) && isUUID(element.value)) {
        return element.value
    } else {
        return undefined
    }
}

export const isTrust = (distributionType: DistributionType) => {
    return distributionType !== DistributionType.CHARITY && distributionType !== DistributionType.OUTRIGHT;
}

export const isNonTrustDistribution = (distributionType: DistributionType) => {
    return distributionType === DistributionType.CHARITY || distributionType === DistributionType.OUTRIGHT;
}

export const createTrust = (distributionType: DistributionType, beneficiaryId?: string): Trust => (
    {
        distributionType: distributionType,
        name: "",
        tags: "Exempt",
        trustees: [],
        successors: [],
        beneficiaries: [],
        funding: "",
        distributions: "",
        termination: "",
        powersOfAppointment: [],
        beneficiaryId: beneficiaryId
    }
);

export const createNonTrustDistribution = (distributionType: DistributionType, overrides?: Partial<NonTrustDistribution>): NonTrustDistribution => (
    {
        distributionType: distributionType,
        name: undefined,
        ...overrides
    } as NonTrustDistribution
);

export const findIrrevocableTrustById = (trustId: string | undefined, flowchart: EstateFlowchartState) => {
    return flowchart.irrevocableTrusts?.find(trust => trust.trustId === trustId) || null;
}

export const findMiscellaneousById = (miscId: string | undefined, flowchart: EstateFlowchartState) => {
    return flowchart.wildcardTrusts?.find(misc => misc.trustId === miscId) || null;
}

export const findWillById = (willId: string, flowchart: EstateFlowchartState) => {
    if (flowchart.willSummary && flowchart.willSummary.primaryWill?.trustId === willId)
        return flowchart.willSummary.primaryWill;
    if (flowchart.willSummary && flowchart.willSummary.partnerWill?.trustId === willId)
        return flowchart.willSummary.partnerWill;
    return null;
}

export const findTrustInFlowchart = (
    trustId: string | undefined,
    estateItemId: string,
    estateItemType: FlowchartEstateItemType,
    flowchart: EstateFlowchartState
) => {
    if (estateItemType === FlowchartEstateItemType.REVOCABLE) {
        return (
            flowchart.revocableTrust?.beneficiaries?.firstDeathDistributions.find(
                distribution => distribution.distributionId === trustId
            ) ?? flowchart.revocableTrust?.beneficiaries?.remainderDistributions.find(
                distribution => distribution.distributionId === trustId
            ) ?? flowchart.revocableTrust?.beneficiaries?.additionalDistributions?.distributions?.find(
                distribution => distribution.distributionId === trustId
            ) ?? null
        );
    }
    if (estateItemType === FlowchartEstateItemType.IRREVOCABLE) {
        const irrevocableTrust = findIrrevocableTrustById(estateItemId, flowchart);
        return (
            irrevocableTrust?.firstDeathBeneficiaries?.firstDeathDistributions.find(
                distribution => distribution.distributionId === trustId
            ) ?? null
        );
    }
};

export const convertToDistributions = (oldValue: {
    trusts: any[],
    nonTrustDistributions: any[]
}): (Trust | Charity | Outright)[] => {
    const newTrusts: Trust[] = oldValue.trusts.map((trust) => {
        const newTrust: Trust = {
            name: trust.trustName,
            distributionType: trust.distributionType,
            tags: trust.tags,
            trustees: trust.trustees,
            successors: trust.successors,
            beneficiaries: trust.beneficiaries,
            funding: trust.funding,
            distributions: trust.distributions,
            termination: trust.termination,
            powersOfAppointment: trust.powersOfAppointment,
            beneficiaryId: trust.beneficiaryId,
            distributionId: trust.trustId
        };
        return newTrust;
    })
    return [...newTrusts, ...oldValue.nonTrustDistributions];
}

export const convertFromDistributions = (oldValue: (Trust | Charity | Outright)[]): {
    trusts: any[],
    nonTrustDistributions: any[]
} => {
    const newTrusts: any[] = oldValue.filter((distribution) => isTrust(distribution.distributionType)).map((trust) => {
        const tmp = trust as Trust;
        const newTrust: any = {
            trustName: tmp.name,
            distributionType: tmp.distributionType,
            tags: tmp.tags,
            trustees: tmp.trustees,
            successors: tmp.successors,
            beneficiaries: tmp.beneficiaries,
            funding: tmp.funding,
            distributions: tmp.distributions,
            termination: tmp.termination,
            powersOfAppointment: tmp.powersOfAppointment,
            beneficiaryId: tmp.beneficiaryId,
            trustId: tmp.distributionId
        };
        return newTrust;
    })
    const newNonTrustDistributions = oldValue.filter((distribution) => !isTrust(distribution.distributionType));
    return {trusts: newTrusts, nonTrustDistributions: newNonTrustDistributions};
}

export const clearPowersOfAppointmentId = (powersOfAppointment: PowerOfAppointment[]) => {
    return powersOfAppointment.map(poa => ({...poa, poaId: ""}));
}

/**
 * Parses the family members from the given data and returns an object containing
 * the primary member's value and label, along with an array of additional family members.
 *
 * @param data - The data containing information about the primary member, partner member, and additional members from the clientManagementApiClient.getMemberGroup endpoint.
 * @returns An object containing the primary member's value and label, and an array of family members.
 */
export const parseFamilyMembers = (data: MemberGroup): FamilyMembers => {
    if (!data || data === {} as MemberGroup) {
        return {} as FamilyMembers;
    }
    const value = data.primaryMember?.id;
    const label = data.primaryMember?.firstName + " " + data.primaryMember?.lastName;
    let familyMembers: Member[] = data.additionalMembers?.flatMap((member: any) => {
        return [
            {
                value: member.id,
                label: member.firstName + " " + member.lastName,
                selected: false
            }
        ];
    })
    if (data.partnerMember) {
        familyMembers.push({
            value: data.partnerMember.id,
            label: data.partnerMember.firstName + " " + data.partnerMember.lastName,
            selected: false
        });
    }

    return {value, label, familyMember: Array.from(new Set(familyMembers))};
}


/**
 * Returns a custom member Name object if the provided member Id is saved as custom Name in the backend
 *
 * @param customName - The custom member name or ID.
 * @param familyMembers -family members from the memberGroup.
 * @returns An object with `label` and `value` set to the custom name if it is not found in the family members, otherwise `null`.
 */
const getCustomMemberName = (customName: string | undefined, familyMembers: Member[]) => {
    if (customName && !familyMembers.some(member => member.value === customName)) {
        return {label: customName, value: customName};
    }
    return null;
};

/**
 * Generates an array of dropdown items for family members, including a custom member if provided.
 *
 * @param familyMembers - The list of family members to include in the dropdown.
 * @param selectedMemberIdOrName - The ID or name of a custom member to include if not already present.
 * @param includeCustom - When true, include a custom member value if provided.
 * @returns An array of DropdownItem objects representing the family members and any custom member.
 */
export const createFamilyMemberDropdownItems = (
    familyMembers: Member[],
    selectedMemberIdOrName: string | undefined,
    includeCustom: boolean = true
) => {
    const items: DropdownItem[] = [];
    if (familyMembers === undefined || familyMembers.length === 0) {
        return items;
    }

    familyMembers.forEach((member) => {
        if (member.label && member.value) {
            const dropdownItem: DropdownItem = {itemText: member.label, value: member.value};
            items.push(dropdownItem);
        }
    });
    if (includeCustom) {
        const customMember = getCustomMemberName(selectedMemberIdOrName, familyMembers);
        if (customMember?.label && customMember?.value) {
            items.push({itemText: customMember.label, value: customMember.value});
        }
    }
    return items;
}

export const parseStates = (data: any) => {
    let listOfStates: State[] = [];
    for (const key in data) {
        const item: State = {
            value: key,
            itemText: data[key]
        };
        listOfStates.push(item);
    }
    listOfStates.sort((a, b) => a.itemText.localeCompare(b.itemText))
    return listOfStates;
}

export const getPrimaryAndPartnerMemberNames = (familyMembers: MemberGroup) => {
    const primaryMemberName = familyMembers.primaryMember?.firstName + " " + familyMembers.primaryMember?.lastName
    const partnerMemberName = familyMembers.partnerMember?.firstName + " " + familyMembers.partnerMember?.lastName;
    return {primaryMemberName, partnerMemberName};
}

export const flattenMemberGroup = (familyMemberGroup: MemberGroup) => {
    const familyMembersMap = new Map<string, MemberGroupMember>();

    const primaryMember = familyMemberGroup.primaryMember;
    const partnerMember = familyMemberGroup.partnerMember;
    const additionalMembers = familyMemberGroup.additionalMembers;

    if (primaryMember !== null) familyMembersMap.set(primaryMember.id, primaryMember);
    if (partnerMember !== null) familyMembersMap.set(partnerMember.id, partnerMember);
    additionalMembers.forEach((additionalMember: MemberGroupMember) => {
        if (additionalMember !== null) familyMembersMap.set(additionalMember.id, additionalMember);
    })

    return familyMembersMap;
}

export const getFamilyMemberByMemberId = (memberId: string | undefined | null, familyMemberGroup: MemberGroup): MemberGroupMember | undefined => {
    if (memberId === undefined || memberId === null) return undefined;
    const familyMembersMap = flattenMemberGroup(familyMemberGroup);
    return familyMembersMap.get(memberId);
}

export const getAdditionalDistributionTriggerTitle = (
    triggerEventData: AdditionalDistributionLayer,
    memberName: string | undefined
): string => {
    switch (triggerEventData.triggerEventType) {
        case "DEATH":
            return memberName ? `Beneficiaries at ${memberName}'s Death` : "Additional Distribution";
        case "AGE_ATTAINMENT":
            return memberName && triggerEventData.triggerAge !== null && triggerEventData.triggerAge !== undefined
                ? `Beneficiaries After ${memberName} Turns ${triggerEventData.triggerAge}`
                : "Additional Distribution";
        case "OTHER":
            return triggerEventData.triggerEventText?.trim()
                ? `Beneficiaries After ${triggerEventData.triggerEventText.trim()}`
                : "Additional Distribution";
        default:
            return "Additional Distribution";
    }
};

export const getDistributionItemTypeName = (distributionType: DistributionType) => {
    const typeToNameMap = new Map<DistributionType, string>();
    typeToNameMap.set(DistributionType.CREDIT_SHELTER, "Credit Shelter Trust");
    typeToNameMap.set(DistributionType.IN_TRUST, "In Trust");
    typeToNameMap.set(DistributionType.MARITAL, "Marital Trust");
    typeToNameMap.set(DistributionType.SURVIVOR, "Survivor's Trust");
    typeToNameMap.set(DistributionType.CHARITY, "Charity");
    typeToNameMap.set(DistributionType.OUTRIGHT, "Outright");
    return typeToNameMap.get(distributionType) ?? "";
}

export const getMemberNameById = (memberGroup: MemberGroup, memberId: string): string | null => {
    if (memberGroup.primaryMember?.id === memberId) {
        return `${memberGroup.primaryMember.firstName} ${memberGroup.primaryMember.lastName}`;
    }

    if (memberGroup.partnerMember?.id === memberId) {
        return `${memberGroup.partnerMember.firstName} ${memberGroup.partnerMember.lastName}`;
    }

    if (memberGroup.additionalMembers) {
        const additionalMember = memberGroup.additionalMembers.find(member => member.id === memberId);
        if (additionalMember) {
            return `${additionalMember.firstName} ${additionalMember.lastName}`;
        }
    }
    return null;
};

export const resolveTriggerMemberName = (
    memberGroup: MemberGroup,
    triggerMemberId?: string,
    triggerMemberName?: string
): string | undefined => {
    const resolvedName = getMemberNameById(memberGroup, triggerMemberId ?? "");
    if (resolvedName) {
        return resolvedName;
    }

    return !triggerMemberId ? triggerMemberName : undefined;
};

export const getMembersList = (memberGroup: MemberGroup, estateMembers: EstateMember[]): string[] => {
    let memberNames: string[] = [];

    estateMembers.forEach((estateMember) => {
        if (estateMember.memberId) {
            const memberName = getMemberNameById(memberGroup, estateMember.memberId)
            if (memberName) {
                memberNames.push(memberName);
            } else if (estateMember.customName) {
                memberNames.push(estateMember.customName);
            }
        }
    });

    return memberNames;
}

export const getOrderedEstateMemberList = (memberGroup: MemberGroup, estateMembers: EstateMember[]): string[] => {
    const sortedMembers = estateMembers
        .filter((item) => item.memberOrder !== undefined)
        .sort((a, b) => (a.memberOrder ?? 0) - (b.memberOrder ?? 0));

    const membersByOrder: Record<number, EstateMember[]> = {};

    sortedMembers.forEach((item) => {
        const order = item.memberOrder!;
        if (!membersByOrder[order]) {
            membersByOrder[order] = [];
        }
        membersByOrder[order].push(item);
    });

    return Object.entries(membersByOrder).map(([_, items]) =>
        getMembersList(memberGroup, items).join(", ")
    );
}

export const flattenMembers = (memberGroup: MemberGroup, trustees: Trustee[]) => {
    return getMembersList(memberGroup, trustees).join(", ");
}

export const convertStateAbbreviation = (unitedStates: any, stateAbbreviation: string) => {
    const parsedStates = parseStates(unitedStates);
    return parsedStates.find((state) => state.value === stateAbbreviation)?.itemText;
}

export const hasWillSummary = (willSummary: WillSummary | undefined): {
    hasWills: boolean,
    hasPrimaryWill: boolean,
    hasPartnerWill: boolean
} => {
    const hasPrimaryWill = willSummary?.primaryWill !== undefined && willSummary?.primaryWill !== null;
    const hasPartnerWill = willSummary?.partnerWill !== undefined && willSummary?.partnerWill !== null;
    const hasWills = hasPrimaryWill || hasPartnerWill;
    return {hasWills, hasPrimaryWill, hasPartnerWill};
}

const hasFiduciaries = (fiduciarySummaryMember?: FiduciaryMember[]) => {
    return fiduciarySummaryMember && fiduciarySummaryMember.length > 0;
};

export const memberHasAnyFiduciaries = (fiduciaryAssignment?: FiduciaryAssignment): boolean => {
    return !!(hasFiduciaries(fiduciaryAssignment?.executors) ||
        hasFiduciaries(fiduciaryAssignment?.guardiansForMinors) ||
        hasFiduciaries(fiduciaryAssignment?.trustees) ||
        hasFiduciaries(fiduciaryAssignment?.financialPowerOfAttorneys) ||
        hasFiduciaries(fiduciaryAssignment?.healthcarePowerOfAttorneys));
}

/**
 * Recursively compares two objects or values and logs any differences found.
 * For each mismatch, it prints the path to the differing property and the values.
 *
 * @param a - The first object or value to compare.
 * @param b - The second object or value to compare.
 * @param path - The current property path being compared (used internally for recursion).
 */
export const logDiff = (a: any, b: any, path = '') => {
    if (typeof a !== 'object' || typeof b !== 'object' || a == null || b == null) {
        if (a !== b) console.log(`Mismatch at ${path}: ${a} !== ${b}`);
        return;
    }
    const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
    for (const key of keys) {
        logDiff(a[key], b[key], path ? `${path}.${key}` : key);
    }
}

export const emptyWill: Will = {
    trustId: "",
    flowchartName: "",
    willOwner: "",
    pourOverWill: false,
    primaryWill: false,
    legalName: "",
    nickName: "",
    creationDate: "",
    residuaryDistribution: "",
}

export const emptyRevocableTrust: RevocableTrust = {
    trustId: "",
    flowchartName: "",
    legalName: "",
    nickName: "",
    trustCreationDate: "",
    recordOfAmendment: "",
    grantors: [],
    trustees: [],
    successors: [],
    beneficiaries: {
        firstDeathDistributions: [],
        remainderDistributions: []
    },
    funding: "",
    investmentAdvisor: "",
    trustProtector: "",
    trustJurisdiction: undefined,
    assumedFirstDeath: "",
    hasBeneficiariesAtFirstDeath: false,
}

export const emptyIrrev: IrrevocableTrust = {
    trustId: "",
    flowchartName: "",
    legalName: "",
    nickName: "",
    trustCreationDate: "",
    recordOfAmendment: "",
    tags: "Exempt",
    grantors: [],
    trustees: [],
    successors: [],
    beneficiaries: [],
    funding: "",
    distributions: "",
    terminatesUponSpecificDeath: false,
    terminationDetails: "",
    firstDeathTerminationMember: undefined,
    investmentAdvisor: "",
    trustProtector: "",
    trustJurisdiction: undefined,
    powersOfAppointment: [],
    firstDeathBeneficiaries: null,
}

export const emptyMiscellaneous: Miscellaneous = {
    trustId: "",
    flowchartName: "",
    name: "",
    estateToggleType: EstateToggle.IN_ESTATE,
    visualCardRepresentation: "Miscellaneous",
    beneficiaries: [],
    funding: "",
    additionalDetails: ""
}

/**
 * Filters the wildcardTrusts array by the given estateToggleType.
 * @param wildcardTrusts - The array of wildcard trusts to filter.
 * @param estateToggleType - The estate toggle type to filter by (e.g., IN_ESTATE or OUT_OF_ESTATE).
 * @returns The filtered array of wildcard trusts matching the estateToggleType.
 */
export const filterWildcardTrustsByEstateToggleType = (
    wildcardTrusts: Miscellaneous[] | undefined,
    estateToggleType: EstateToggle
): Miscellaneous[] => {
    if (!wildcardTrusts) return [];
    return wildcardTrusts.filter(trust => trust.estateToggleType === estateToggleType);
};
